import { Code2, Server, Database, Cloud, Container, GitBranch, Terminal, Shield, Cpu, Network, Blocks, Workflow, Globe, Layers, Zap, Award, BookOpen, Users, Rocket, Target, TrendingUp, Lock, Wrench, Search, Send, Settings, Box, Braces, FileCode, Laptop, HardDrive, Radio, Gauge } from 'lucide-react';

export const profile = {
  name: 'Teja Surisetty',
  title: 'Senior Platform Engineer',
  email: 'teja@surisetty.dev',
  phone: '+91 8790066042',
  location: 'Bengaluru, India',
  level: 8,
  xp: 8200,
  maxXp: 10000,
  class: 'Eternal Builder',
  classSubtitle: 'Developer at heart. Architect by ambition.',
  tagline: 'From Blockchain to Ultra-Low Latency — Engineering systems that move at the speed of thought.',
  industries: ['Fintech', 'Cybersecurity', 'Capital Markets', 'Enterprise Blockchain', 'Microfinance'],
  summary: 'Platform engineer with 8+ years of experience across fintech, cybersecurity, capital markets, and enterprise blockchain. Led a 20-engineer team on a $2M initiative and delivered measurable gains in throughput, latency, and operational efficiency. Strongest in Go, Kubernetes, distributed systems, and Hyperledger Fabric.',
  avatarInitials: 'TS',
  stats: {
    yearsXP: '8+',
    projectsShipped: '15+',
    teamLed: '20+',
    systemEfficiency: '30%'
  }
};

export const skills = [
  { name: 'Golang', level: 95, category: 'language', icon: Code2 },
  { name: 'Rust', level: 72, category: 'language', icon: Cpu },
  { name: 'JavaScript', level: 85, category: 'language', icon: Braces },
  { name: 'Java', level: 78, category: 'language', icon: FileCode },
  { name: 'Node.js', level: 82, category: 'language', icon: Server },
  { name: 'Kubernetes', level: 92, category: 'devops', icon: Container },
  { name: 'Docker', level: 90, category: 'devops', icon: Container },
  { name: 'Terraform', level: 78, category: 'devops', icon: Layers },
  { name: 'CI/CD', level: 88, category: 'devops', icon: GitBranch },
  { name: 'Jenkins', level: 82, category: 'devops', icon: Settings },
  { name: 'Hyperledger Fabric', level: 90, category: 'blockchain', icon: Blocks },
  { name: 'R3 Corda', level: 82, category: 'blockchain', icon: Lock },
  { name: 'Ethereum', level: 75, category: 'blockchain', icon: Globe },
  { name: 'Microservices', level: 94, category: 'architecture', icon: Network },
  { name: 'gRPC', level: 88, category: 'architecture', icon: Zap },
  { name: 'Kafka', level: 84, category: 'architecture', icon: Radio },
  { name: 'Event-Driven Systems', level: 86, category: 'architecture', icon: Gauge },
  { name: 'RabbitMQ', level: 80, category: 'architecture', icon: Workflow },
  { name: 'AWS', level: 85, category: 'cloud', icon: Cloud },
  { name: 'Azure', level: 75, category: 'cloud', icon: Globe },
  { name: 'DigitalOcean', level: 78, category: 'cloud', icon: Cloud },
  { name: 'PostgreSQL', level: 85, category: 'database', icon: Database },
  { name: 'MongoDB', level: 82, category: 'database', icon: Database },
  { name: 'Elasticsearch', level: 78, category: 'database', icon: Search },
  { name: 'Linux', level: 90, category: 'systems', icon: Terminal },
  { name: 'Shell Scripting', level: 85, category: 'systems', icon: Terminal },
  { name: 'Git', level: 92, category: 'tools', icon: GitBranch },
  { name: 'GitHub', level: 90, category: 'tools', icon: GitBranch },
  { name: 'GitLab', level: 88, category: 'tools', icon: GitBranch },
  { name: 'GitHub Actions', level: 85, category: 'tools', icon: Workflow },
  { name: 'DataGrip', level: 80, category: 'tools', icon: Laptop },
  { name: 'Postman', level: 88, category: 'tools', icon: Send },
  { name: 'Composer', level: 76, category: 'tools', icon: Box },
];

export const experience = [
  {
    id: 'benzinga',
    company: 'Benzinga',
    title: 'Senior Platform Engineer',
    location: 'US, Detroit (Remote)',
    period: '2024 - Present',
    type: 'ACTIVE_QUEST',
    xpReward: 2000,
    description: 'Building low-latency Go services and financial data infrastructure for real-time market products.',
    achievements: [
      'Architecting low-latency financial data pipelines that process millions of events per second',
      'Designing high-throughput Go microservices for real-time market data delivery',
      'Optimizing critical-path latency to sub-millisecond response times for financial APIs',
      'Leading backend platform engineering initiatives across distributed teams',
    ],
    techStack: ['Golang', 'Microservices', 'gRPC', 'Kubernetes', 'PostgreSQL', 'Redis'],
    questIcon: Rocket,
  },
  {
    id: 'newstreet',
    company: 'New Street Technologies',
    title: 'Lead Developer',
    location: 'Bengaluru, Karnataka',
    period: 'Nov 2022 - 2024',
    type: 'COMPLETED',
    xpReward: 2500,
    description: 'Championed fintech platform development with enterprise blockchain for a leading bank.',
    achievements: [
      'Reduced transaction processing time by 30% through a high-throughput application redesign',
      'Optimized system efficiency by 25% with gRPC for internal microservice communication',
      'Cut response times by 50% for critical banking flows including CIF, loan, disbursement, and IMPS',
      'Built a blockchain accelerator that increased private-network transaction throughput by 30%',
      'Led a 20-member team from inception to go-live using Agile delivery practices',
      'Strengthened data security and reduced exposure to financial risk by 30%',
    ],
    techStack: ['Golang', 'Gin', 'Hyperledger Fabric', 'Kubernetes', 'gRPC', 'Microservices'],
    questIcon: Shield,
  },
  {
    id: 'ltimindtree',
    company: 'LTIMindtree',
    title: 'Senior Software Engineer',
    location: 'Bengaluru, Karnataka',
    period: 'Jan 2019 - Nov 2022',
    type: 'COMPLETED',
    xpReward: 1500,
    description: 'Co-led development of cutting-edge in-house products with Hyperledger Fabric.',
    achievements: [
      'Automated Kubernetes workflows and reduced deployment time by 20%',
      'Built a low-code platform for seamless Hyperledger Fabric network migration from v1.4 to v2.2+',
      'Improved overall operational efficiency by 25%',
      'Standardized the Go REST service layer and improved scalability by 50%',
      'Contributed widely-adopted open-source automation factory',
    ],
    techStack: ['Hyperledger Fabric', 'Golang', 'Kubernetes', 'Node.js', 'Docker'],
    questIcon: Cpu,
  },
  {
    id: 'opentext',
    company: 'OpenText',
    title: 'Engineering Intern',
    location: 'Hyderabad',
    period: 'Jun 2018 - Dec 2018',
    type: 'COMPLETED',
    xpReward: 200,
    description: 'Improved automation processes and enhanced testing strategies.',
    achievements: [
      'Developed Selenium-based jobs for modular image extraction using OCR',
      'Integrated Robot Framework and Cypress into the broader testing workflow',
    ],
    techStack: ['Selenium', 'Cypress', 'Robot Framework', 'OCR'],
    questIcon: BookOpen,
  },
];

export const achievements = [
  {
    id: 'ckd',
    title: 'Certified Kubernetes Application Developer',
    issuer: 'Linux Foundation',
    date: 'Dec 2023',
    expires: 'Dec 2026',
    icon: Container,
    rarity: 'LEGENDARY',
    color: '#f59e0b',
  },
  {
    id: 'cka',
    title: 'Certified Kubernetes Administrator',
    issuer: 'Linux Foundation',
    date: 'Jan 2022',
    expires: 'Jan 2025',
    icon: Shield,
    rarity: 'EPIC',
    color: '#a855f7',
  },
  {
    id: 'chfa',
    title: 'Certified Hyperledger Fabric Admin',
    issuer: 'Linux Foundation',
    date: 'Nov 2019',
    expires: 'Nov 2022',
    icon: Blocks,
    rarity: 'RARE',
    color: '#06b6d4',
  },
  {
    id: 'r3corda',
    title: 'R3 Corda Certified Developer',
    issuer: 'R3',
    date: 'Earned',
    expires: 'No Expiry',
    icon: Lock,
    rarity: 'EPIC',
    color: '#ec4899',
  },
  {
    id: 'jenkins',
    title: 'Jenkins Certified Engineer',
    issuer: 'CloudBees',
    date: 'Earned',
    expires: 'No Expiry',
    icon: Settings,
    rarity: 'RARE',
    color: '#22c55e',
  },
];

export const education = {
  degree: 'Bachelor of Technology in Electronics and Communication Engineering',
  institution: 'Jawaharlal Nehru Technological University, Kakinada, AP, India',
  location: 'Visakhapatnam, AP',
  period: 'Aug 2014 - May 2018',
  grade: '75.10%',
  achievement: 'Graduated with Distinction',
};

export const contactInfo = {
  heading: 'Initiate Co-op Quest',
  subheading: 'Looking to collaborate on high-performance systems, blockchain platforms, or cutting-edge Go projects? Drop a quest request.',
  email: 'teja@surisetty.dev',
  phone: '+91 8790066042',
};

export const hobbies = {
  aboutMe: 'Beyond the terminal, I\'m either fragging enemies in competitive shooters, exploring open worlds, or soldering circuits together. The same precision that goes into writing Go code fuels my off-duty adventures.',
  games: [
    { name: 'Valorant', genre: 'Tactical FPS', current: true },
    { name: 'Call of Duty', genre: 'FPS', current: false },
    { name: 'Battlefield', genre: 'FPS', current: false },
    { name: 'Tom Clancy\'s Division', genre: 'Open World RPG', current: false },
    { name: 'GTA V', genre: 'Open World', current: false },
  ],
  builds: [
    {
      name: 'FPV Drone',
      description: 'Built a custom FPV drone from scratch. V1 wasn\'t the smoothest flyer — let\'s just say the landing was... unplanned. Version 2 is in the works with improved flight controller.',
      icon: 'Rocket',
      status: 'WIP',
      version: 'v2 coming soon',
    },
    {
      name: 'Custom Radio',
      description: 'Assembled an FM radio receiver from individual components. Tuning into frequencies the old-school way.',
      icon: 'Radio',
      status: 'DONE',
    },
    {
      name: 'Joystick Controller',
      description: 'Built a custom joystick controller with analog inputs for flight simulators and gaming. Soldered, wired, and programmed from scratch.',
      icon: 'Joystick',
      status: 'DONE',
    },
  ],
};
